/**
 * @fileoverview Main Application Router Component
 * @version 1.0.0
 *
 * @description
 * Root component that handles routing configuration for the loan management application.
 * Sets up protected and public routes using React Router.
 *
 * @component App
 *
 * @example
 * // Usage in index.js:
 * import App from './App';
 *
 * ReactDOM.render(
 *   <App />,
 *   document.getElementById('root')
 * );
 *
 * @example
 * // Route navigation example:
 * // To navigate to dashboard:
 * navigate('/dashboard')
 *
 * // To check loan status:
 * navigate('/loan-status/approved')
 */

import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import LoanStatus from "./pages/LoanStatus";
import GeneralInformation from "./pages/general-information";
import "./styles/styles.css";
import "./App.css";
// import Auth from './pages/auth';
import Auth from "./components/Auth";
// for sso
import SAMLCallback from "../src/pages/SAMLCallback";
import { Amplify } from "aws-amplify";
import awsConfig from "./awsConfig";
import AuthGuard from "./guards/AuthGuard";
import { AuthProvider } from "./context/AuthContext";
import { useSessionHandler } from "./hooks/useSessionHandler";
import configManager from "./config/configManager";
import Logout from "../src/pages/Logout";
import GlassBox from "./pages/glassbox";
import Admin from "./pages/Admin/admin";
import {
  SessionTimeoutProvider,
  useSessionTimeout,
} from "./context/SessionTimeoutContext";
import SessionTimeoutModal from "./components/SessionTimeoutModal";
import ProtectedRoute from './common/ProtectedRoute';
configManager.setEnvironment("local"); // or 'dev', 'uat', 'prod'
// Configure Amplify
Amplify.configure({
  ...awsConfig,
  Auth: {
    ...awsConfig.Auth,
    logging: true,
  },
});
const SessionHandler = ({ children }) => {
  useSessionHandler();
  return children;
};
const SessionTimeoutWrapper = () => {
  const { showWarning, extendSession, logout } = useSessionTimeout();
  const location = useLocation();
  // Hide modal for login routes
  const hideForLogin =
    location.pathname === "/" || location.pathname === "/login";

  if (hideForLogin) return null;

  return (
    <SessionTimeoutModal
      show={showWarning}
      onExtend={extendSession}
      onLogout={logout}
    />
  );
};
const App = () => {
  return (
    // Router wrapper with v7 feature flags enabled for better routing capabilities
    // <Router future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
    //   <Routes>
    //     {/* Public route - Login page serves as the entry point */}
    //     <Route path="/" element={<Login />} />
    //     <Route path="/login" element={<Login />} />

    //     {/* Protected route - Dashboard accessible after authentication */}
    //     <Route path="/dashboard" element={<Dashboard />} />

    //     {/* Dynamic route - Loan status page with status parameter */}
    //     <Route path="/loan-status/:status" element={<LoanStatus />} />
    //     <Route path="/general-information" element={<GeneralInformation />} />
    //     <Route path="/auth" element={<Auth />} />
    //   </Routes>
    // </Router>
    <AuthProvider>
      {/* // Router wrapper with v7 feature flags enabled for better routing capabilities */}
      <Router future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
        <SessionTimeoutProvider>
          <SessionHandler>
            <AuthGuard>
              <Routes>
                {/* Public route - Login page serves as the entry point */}
                <Route path="/" element={<Login />} />
                <Route path="/login" element={<Login />} />

                {/* Protected route - Dashboard accessible after authentication */}
                 {/* DGV2-668 */}
                <Route element={<ProtectedRoute isAdmin={false} />}>
                  <Route exact path="/dashboard" element={<Dashboard />} />
                  <Route path="/general-information" element={<GeneralInformation />} />
                </Route>
                {/* DGV2-668 */}

                {/* Dynamic route - Loan status page with status parameter */}
                <Route path="/loan-status/:status" element={<LoanStatus />} />
                
                <Route path="/auth" element={<Auth />} />
                <Route path="/saml-callback" element={<SAMLCallback />} />
                <Route path="/logout" element={<Logout />} />
                <Route path="/glassbox-viewer" element={<GlassBox />} />
                {/* DGV2-668 */}
                <Route element={<ProtectedRoute isAdmin={true} />}>
                  <Route exact path="/admin" element={<Admin />} />
                </Route>
                {/* DGV2-668 */}
              </Routes>
            </AuthGuard>
          </SessionHandler>
          <SessionTimeoutWrapper />
        </SessionTimeoutProvider>
      </Router>
    </AuthProvider>
  );
};
export default App;
